import React, { useEffect, useMemo, useState } from "react";
import { School, GraduationCap, Users, BarChart3, Plus, Trash2, KeyRound, Pencil, Clock, CheckCircle2, XCircle, PlayCircle, CalendarClock, PenLine, QrCode, Mail } from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SignaturePad } from "@/components/SignaturePad";
import { Badge2 } from "@/components/ComplianceBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const fmtH = (s) => `${Math.floor((s || 0) / 3600)}h ${Math.floor(((s || 0) % 3600) / 60)}m`;

function CourseEditor({ course, onClose, onSaved }) {
  const [c, setC] = useState(() => JSON.parse(JSON.stringify(course)));
  const [saving, setSaving] = useState(false);
  const setModule = (i, key, v) => setC((p) => { const m = [...p.modules]; m[i] = { ...m[i], [key]: v }; return { ...p, modules: m }; });
  const addModule = (type = "video") => setC((p) => ({ ...p, modules: [...p.modules, { id: crypto.randomUUID(), theme: "", title: type === "reading" ? "Nova leitura" : "Nova aula em vídeo", type, video_url: "", content: "", duration_min: type === "reading" ? 30 : 45, order: p.modules.length }] }));
  const delModule = (i) => setC((p) => ({ ...p, modules: p.modules.filter((_, x) => x !== i) }));
  const setQ = (i, key, v) => setC((p) => { const q = [...p.questions]; q[i] = { ...q[i], [key]: v }; return { ...p, questions: q }; });
  const setOpt = (qi, oi, v) => setC((p) => { const q = [...p.questions]; const o = [...q[qi].options]; o[oi] = v; q[qi] = { ...q[qi], options: o }; return { ...p, questions: q }; });

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/courses/${c.id}`, c);
      toast.success("Curso atualizado");
      onSaved();
      onClose();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[760px]" data-testid="course-editor">
        <DialogHeader><DialogTitle className="text-[#0B2545]">{c.nr} — Editar curso</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="mb-1 block text-xs font-semibold text-slate-600">Resumo da NR</Label>
            <Textarea rows={4} value={c.summary} onChange={(e) => setC({ ...c, summary: e.target.value })} data-testid="course-summary" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label className="mb-1 block text-xs font-semibold text-slate-600">Horas (segundos)</Label>
              <Input type="number" value={c.required_seconds} onChange={(e) => setC({ ...c, required_seconds: Number(e.target.value) })} /></div>
            <div><Label className="mb-1 block text-xs font-semibold text-slate-600">Acertos p/ aprovar</Label>
              <Input type="number" value={c.pass_score} onChange={(e) => setC({ ...c, pass_score: Number(e.target.value) })} /></div>
            <div><Label className="mb-1 block text-xs font-semibold text-slate-600">Validade (meses, 0=sem)</Label>
              <Input type="number" value={c.validity_months ?? 0} onChange={(e) => setC({ ...c, validity_months: Number(e.target.value) })} data-testid="course-validity" /></div>
          </div>
          <div>
            <Label className="mb-1 block text-xs font-semibold text-slate-600">Responsável técnico (assina o certificado)</Label>
            <Input value={c.responsible_name || ""} onChange={(e) => setC({ ...c, responsible_name: e.target.value })} placeholder="Nome do Técnico de Segurança do Trabalho" data-testid="course-responsible" />
          </div>
          <div>
            <Label className="mb-1 block text-xs font-semibold text-slate-600">Conteúdo programático (1 tópico por linha — vale no certificado)</Label>
            <Textarea rows={5} value={c.content_outline || ""} onChange={(e) => setC({ ...c, content_outline: e.target.value })} placeholder={"Deixe em branco para usar os tópicos automáticos.\nEx.:\nObjetivo e campo de aplicação da NR\nResponsabilidades\nMedidas de controle\nAvaliação final"} data-testid="course-outline" />
          </div>
          <div>
            <div className="mb-1 flex items-center justify-between"><Label className="text-xs font-semibold text-slate-600">Aulas e material (vídeos e leituras)</Label>
              <div className="flex gap-1.5">
                <Button type="button" size="sm" variant="outline" onClick={() => addModule("video")} data-testid="course-add-video"><Plus className="mr-1 h-3.5 w-3.5" /> Vídeo</Button>
                <Button type="button" size="sm" variant="outline" onClick={() => addModule("reading")} data-testid="course-add-reading"><Plus className="mr-1 h-3.5 w-3.5" /> Leitura</Button>
              </div></div>
            <div className="space-y-3">
              {c.modules.map((m, i) => (
                <div key={m.id} className="rounded-lg border border-slate-200 p-3" data-testid={`course-module-${i}`}>
                  <div className="mb-2 flex items-center gap-2">
                    <span className={`rounded px-2 py-0.5 text-[10px] font-semibold uppercase ${m.type === "reading" ? "bg-amber-100 text-amber-700" : "bg-blue-100 text-blue-700"}`}>{m.type === "reading" ? "Leitura" : "Vídeo"}</span>
                    <Input className="h-8 flex-1" value={m.theme || ""} onChange={(e) => setModule(i, "theme", e.target.value)} placeholder="Tema (ex.: Tema 01 — Responsabilidades)" />
                    <Input className="h-8 w-20" type="number" value={m.duration_min || 0} onChange={(e) => setModule(i, "duration_min", Number(e.target.value))} placeholder="min" title="Duração estimada (min)" />
                    <Button type="button" size="icon" variant="ghost" className="h-8 w-8" onClick={() => delModule(i)}><Trash2 className="h-4 w-4 text-rose-500" /></Button>
                  </div>
                  <Input className="mb-2 h-8" value={m.title} onChange={(e) => setModule(i, "title", e.target.value)} placeholder="Título da aula" />
                  {m.type === "reading" ? (
                    <Textarea rows={4} value={m.content || ""} onChange={(e) => setModule(i, "content", e.target.value)} placeholder="Conteúdo da apostila (pode usar HTML simples: <h4>, <p>, <ul><li>)" data-testid={`course-content-${i}`} />
                  ) : (
                    <Input value={m.video_url} onChange={(e) => setModule(i, "video_url", e.target.value)} placeholder="https://youtube.com/watch?v=..." data-testid={`course-video-${i}`} />
                  )}
                </div>
              ))}
            </div>
          </div>
          <div>
            <Label className="mb-1 block text-xs font-semibold text-slate-600">Prova (5 questões · marque a correta)</Label>
            <div className="space-y-3">
              {c.questions.map((q, qi) => (
                <div key={q.id} className="rounded-lg border border-slate-200 p-3">
                  <Input className="mb-2" value={q.text} onChange={(e) => setQ(qi, "text", e.target.value)} placeholder={`Pergunta ${qi + 1}`} />
                  {q.options.map((o, oi) => (
                    <div key={oi} className="mb-1 flex items-center gap-2">
                      <input type="radio" name={`q-${qi}`} checked={q.correct_index === oi} onChange={() => setQ(qi, "correct_index", oi)} data-testid={`q-${qi}-correct-${oi}`} />
                      <Input value={o} onChange={(e) => setOpt(qi, oi, e.target.value)} />
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={save} disabled={saving} className="bg-[#0B2545] hover:bg-[#134074]" data-testid="course-save">{saving ? "Salvando..." : "Salvar curso"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Cursos() {
  const [courses, setCourses] = useState([]);
  const [editing, setEditing] = useState(null);
  const { canManage } = useAuth();
  const load = () => api.get("/courses").then(({ data }) => setCourses(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="ead-cursos">
      {courses.map((c) => (
        <div key={c.id} className="rounded-xl border border-[#E2E8F0] bg-white p-4 shadow-sm" data-testid={`course-card-${c.nr}`}>
          <div className="flex items-center justify-between">
            <Badge2 color="blue">{c.nr}</Badge2>
            {canManage && <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setEditing(c)} data-testid={`course-edit-${c.nr}`}><Pencil className="h-3.5 w-3.5 text-slate-500" /></Button>}
          </div>
          <h3 className="mt-2 text-sm font-bold text-[#0B2545]">{c.title}</h3>
          <p className="mt-1 line-clamp-3 text-xs text-slate-500">{c.summary}</p>
          <div className="mt-3 flex flex-wrap gap-3 text-[11px] text-slate-400">
            <span>{Math.round((c.required_seconds || 0) / 3600)}h</span>
            <span>{c.questions?.length || 0} questões</span>
            <span>{c.modules?.length || 0} vídeo(s)</span>
            <span>validade {c.validity_months || 0}m</span>
          </div>
        </div>
      ))}
      {editing && <CourseEditor course={editing} onClose={() => setEditing(null)} onSaved={load} />}
    </div>
  );
}

function Atribuicoes() {
  const [courses, setCourses] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [openNew, setOpenNew] = useState(false);
  const [openManual, setOpenManual] = useState(false);
  const [resetId, setResetId] = useState(null);
  const [qrEnr, setQrEnr] = useState(null);
  const { canManage } = useAuth();

  const loadAll = () => {
    api.get("/courses").then(({ data }) => setCourses(data)).catch(() => {});
    api.get("/employees").then(({ data }) => setEmployees(data)).catch(() => {});
    api.get("/enrollments").then(({ data }) => setEnrollments(data)).catch(() => {});
  };
  useEffect(() => { loadAll(); }, []);

  const [f, setF] = useState({ course_id: "", employee_id: "", username: "", password: "" });
  const createOnline = async () => {
    if (!f.course_id || !f.employee_id || !f.username || !f.password) { toast.error("Preencha todos os campos"); return; }
    try {
      const { data } = await api.post("/enrollments", f);
      if (data.email_sent) {
        toast.success(`Matrícula criada e e-mail de acesso enviado ao colaborador! Login: ${f.username}`, { duration: 9000 });
      } else {
        toast.warning(`Matrícula criada (Login: ${f.username} / Senha: ${f.password}). E-mail NÃO enviado: ${data.email_reason || "verifique o e-mail do colaborador"}. Cadastre o e-mail do colaborador e use o botão de reenviar.`, { duration: 13000 });
      }
      setOpenNew(false); setF({ course_id: "", employee_id: "", username: "", password: "" }); loadAll();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const resendEmail = async (id) => {
    try { const { data } = await api.post(`/enrollments/${id}/send-access-email`); toast.success(`E-mail de acesso enviado para ${data.email}`); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Falha ao enviar e-mail"); }
  };

  const [m, setM] = useState({ course_id: "", employee_id: "", completed_at: "", valid_until: "", best_score: "" });
  const createManual = async () => {
    if (!m.course_id || !m.employee_id || !m.completed_at) { toast.error("Curso, colaborador e data são obrigatórios"); return; }
    try {
      await api.post("/enrollments/manual", { ...m, best_score: m.best_score ? Number(m.best_score) : null });
      toast.success("Treinamento registrado (colaborador já treinado)");
      setOpenManual(false); setM({ course_id: "", employee_id: "", completed_at: "", valid_until: "", best_score: "" }); loadAll();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const [newPass, setNewPass] = useState("");
  const doReset = async () => {
    try { await api.put(`/enrollments/${resetId}/reset`, { password: newPass }); toast.success("Senha redefinida"); setResetId(null); setNewPass(""); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const del = async (id) => { try { await api.delete(`/enrollments/${id}`); toast.success("Removido"); loadAll(); } catch { toast.error("Falha"); } };

  return (
    <div className="space-y-4" data-testid="ead-atribuicoes">
      {canManage && (
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => setOpenNew(true)} className="bg-[#0B2545] hover:bg-[#134074]" data-testid="enroll-new-btn"><Plus className="mr-1.5 h-4 w-4" /> Nova matrícula (online)</Button>
          <Button onClick={() => setOpenManual(true)} variant="outline" className="border-emerald-200 text-emerald-700 hover:bg-emerald-50" data-testid="enroll-manual-btn"><CheckCircle2 className="mr-1.5 h-4 w-4" /> Já treinado (registro manual)</Button>
          <a href="/escola" target="_blank" rel="noreferrer" className="inline-flex items-center rounded-md border border-[#134074]/30 px-3 py-1.5 text-sm font-medium text-[#134074] hover:bg-[#134074]/5">Abrir portal do aluno ↗</a>
        </div>
      )}

      <div className="overflow-hidden rounded-xl border border-[#E2E8F0] bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
              <th className="px-4 py-3">Colaborador</th><th className="px-4 py-3">Curso</th><th className="px-4 py-3">Login</th>
              <th className="px-4 py-3">Tipo</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Validade</th><th className="px-4 py-3 text-right">Ações</th>
            </tr></thead>
            <tbody>
              {enrollments.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">Nenhuma matrícula ainda</td></tr>
              ) : enrollments.map((e) => (
                <tr key={e.id} className="border-b border-slate-100 last:border-0" data-testid={`enroll-row-${e.id}`}>
                  <td className="px-4 py-3 font-medium text-slate-700">{e.employee_name}</td>
                  <td className="px-4 py-3">{e.nr}</td>
                  <td className="px-4 py-3 text-slate-500">{e.manual ? "—" : e.username}</td>
                  <td className="px-4 py-3">{e.manual ? <Badge2 color="slate">Manual</Badge2> : <Badge2 color="blue">Online</Badge2>}</td>
                  <td className="px-4 py-3">
                    <Badge2 color={String(e.status).includes("Aprovado") ? "green" : String(e.status).includes("Reprovado") ? "red" : e.status === "Expirado" ? "red" : e.status === "Em andamento" ? "yellow" : "slate"}>{e.status}</Badge2>
                  </td>
                  <td className="px-4 py-3">{e.valid_until ? <Badge2 color={new Date(e.valid_until) < new Date() ? "red" : (new Date(e.valid_until) - new Date()) / 86400000 <= 30 ? "yellow" : "green"}>{e.valid_until}</Badge2> : <span className="text-slate-300">—</span>}</td>
                  <td className="px-4 py-3 text-right">
                    {canManage && !e.manual && e.access_key && <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setQrEnr(e)} title="QR de acesso" data-testid={`enroll-qr-${e.id}`}><QrCode className="h-4 w-4 text-[#134074]" /></Button>}
                    {canManage && !e.manual && <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => resendEmail(e.id)} title="Reenviar e-mail de acesso" data-testid={`enroll-mail-${e.id}`}><Mail className="h-4 w-4 text-emerald-600" /></Button>}
                    {canManage && !e.manual && <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setResetId(e.id)} title="Redefinir senha" data-testid={`enroll-reset-${e.id}`}><KeyRound className="h-4 w-4 text-slate-500" /></Button>}
                    {canManage && <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => del(e.id)} data-testid={`enroll-del-${e.id}`}><Trash2 className="h-4 w-4 text-rose-500" /></Button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={openNew} onOpenChange={setOpenNew}>
        <DialogContent data-testid="enroll-dialog">
          <DialogHeader><DialogTitle className="text-[#0B2545]">Nova matrícula online</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="mb-1 block text-xs font-semibold text-slate-600">Curso</Label>
              <Select value={f.course_id} onValueChange={(v) => setF({ ...f, course_id: v })}><SelectTrigger data-testid="enroll-course"><SelectValue placeholder="Selecione a NR" /></SelectTrigger><SelectContent>{courses.map((c) => <SelectItem key={c.id} value={c.id}>{c.nr} — {c.title}</SelectItem>)}</SelectContent></Select></div>
            <div><Label className="mb-1 block text-xs font-semibold text-slate-600">Colaborador</Label>
              <Select value={f.employee_id} onValueChange={(v) => { const emp = employees.find((x) => x.id === v); setF({ ...f, employee_id: v, username: emp ? emp.name.toLowerCase().split(" ")[0] + "." + (f.course_id ? (courses.find((c) => c.id === f.course_id)?.nr || "").toLowerCase().replace("-", "") : "nr") : f.username }); }}>
                <SelectTrigger data-testid="enroll-emp"><SelectValue placeholder="Selecione o colaborador" /></SelectTrigger><SelectContent className="max-h-[260px]">{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent></Select></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="mb-1 block text-xs font-semibold text-slate-600">Login</Label><Input value={f.username} onChange={(e) => setF({ ...f, username: e.target.value })} data-testid="enroll-username" /></div>
              <div><Label className="mb-1 block text-xs font-semibold text-slate-600">Senha</Label><Input value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="enroll-password" /></div>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpenNew(false)}>Cancelar</Button><Button onClick={createOnline} className="bg-[#0B2545] hover:bg-[#134074]" data-testid="enroll-save">Criar matrícula</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={openManual} onOpenChange={setOpenManual}>
        <DialogContent data-testid="manual-dialog">
          <DialogHeader><DialogTitle className="text-[#0B2545]">Registrar colaborador já treinado</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="mb-1 block text-xs font-semibold text-slate-600">Curso / NR</Label>
              <Select value={m.course_id} onValueChange={(v) => setM({ ...m, course_id: v })}><SelectTrigger data-testid="manual-course"><SelectValue placeholder="Selecione a NR" /></SelectTrigger><SelectContent>{courses.map((c) => <SelectItem key={c.id} value={c.id}>{c.nr} — {c.title}</SelectItem>)}</SelectContent></Select></div>
            <div><Label className="mb-1 block text-xs font-semibold text-slate-600">Colaborador</Label>
              <Select value={m.employee_id} onValueChange={(v) => setM({ ...m, employee_id: v })}><SelectTrigger data-testid="manual-emp"><SelectValue placeholder="Selecione o colaborador" /></SelectTrigger><SelectContent className="max-h-[260px]">{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent></Select></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="mb-1 block text-xs font-semibold text-slate-600">Data da conclusão</Label><Input type="date" value={m.completed_at} onChange={(e) => setM({ ...m, completed_at: e.target.value })} data-testid="manual-date" /></div>
              <div><Label className="mb-1 block text-xs font-semibold text-slate-600">Validade (opcional)</Label><Input type="date" value={m.valid_until} onChange={(e) => setM({ ...m, valid_until: e.target.value })} data-testid="manual-validity" /></div>
            </div>
            <p className="text-xs text-slate-400">Se deixar a validade em branco, usamos a validade padrão do curso. O sistema avisa 30 dias antes do vencimento.</p>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpenManual(false)}>Cancelar</Button><Button onClick={createManual} className="bg-[#0B2545] hover:bg-[#134074]" data-testid="manual-save">Registrar</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!resetId} onOpenChange={(o) => !o && setResetId(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="text-[#0B2545]">Redefinir senha</DialogTitle></DialogHeader>
          <Input value={newPass} onChange={(e) => setNewPass(e.target.value)} placeholder="Nova senha" data-testid="reset-password-input" />
          <DialogFooter><Button variant="outline" onClick={() => setResetId(null)}>Cancelar</Button><Button onClick={doReset} className="bg-[#0B2545] hover:bg-[#134074]" data-testid="reset-password-save">Salvar</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!qrEnr} onOpenChange={(o) => !o && setQrEnr(null)}>
        <DialogContent data-testid="qr-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2 text-[#0B2545]"><QrCode className="h-5 w-5" /> QR de acesso ao treinamento</DialogTitle></DialogHeader>
          {qrEnr && (() => {
            const url = `${window.location.origin}/escola?k=${qrEnr.access_key}`;
            return (
              <div className="flex flex-col items-center gap-3 text-center">
                <p className="text-sm text-slate-600"><b>{qrEnr.employee_name}</b> · {qrEnr.nr}</p>
                <div className="rounded-xl border border-slate-200 bg-white p-4"><QRCodeCanvas value={url} size={220} level="M" marginSize={2} data-testid="qr-canvas" /></div>
                <p className="text-xs text-slate-500">O colaborador aponta a câmera do celular para este QR e abre o treinamento direto — sem digitar login. Em seguida é feita a captura da foto de presença.</p>
                <div className="flex w-full items-center gap-2">
                  <Input readOnly value={url} className="text-xs" data-testid="qr-url" onFocus={(e) => e.target.select()} />
                  <Button variant="outline" onClick={() => { navigator.clipboard?.writeText(url); toast.success("Link copiado"); }} data-testid="qr-copy">Copiar</Button>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Acompanhamento() {
  const [enrollments, setEnrollments] = useState([]);
  useEffect(() => { api.get("/enrollments").then(({ data }) => setEnrollments(data)).catch(() => {}); }, []);
  const online = useMemo(() => enrollments.filter((e) => !e.manual), [enrollments]);
  return (
    <div className="space-y-3" data-testid="ead-acompanhamento">
      {online.length === 0 ? <p className="text-sm text-slate-400">Nenhuma matrícula online para acompanhar.</p> : online.map((e) => {
        const p = e.progress || { percent: 0, accrued_seconds: 0, required_seconds: 28800 };
        return (
          <div key={e.id} className="rounded-xl border border-[#E2E8F0] bg-white p-4 shadow-sm" data-testid={`track-${e.id}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div><p className="text-sm font-bold text-[#0B2545]">{e.employee_name}</p><p className="text-xs text-slate-500">{e.nr} · {e.course_title}</p></div>
              <Badge2 color={String(e.status).includes("Aprovado") ? "green" : String(e.status).includes("Reprovado") ? "red" : e.status === "Expirado" ? "red" : e.status === "Em andamento" ? "yellow" : "slate"}>{e.status}</Badge2>
            </div>
            <div className="mt-3"><div className="mb-1 flex justify-between text-xs text-slate-500"><span><Clock className="mr-1 inline h-3 w-3" />{fmtH(p.accrued_seconds)} / {fmtH(p.required_seconds)}</span><span>{p.percent}%</span></div><Progress value={p.percent} /></div>
            <div className="mt-2 flex flex-wrap gap-3 text-[11px] text-slate-400">
              <span>Tentativas: {e.attempts}</span>
              <span>Melhor nota: {e.best_score ?? "—"}</span>
              <span>Fotos de presença: {e.face_captures}</span>
              {e.valid_until && <span><CalendarClock className="mr-1 inline h-3 w-3" />Validade: {e.valid_until}</span>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Signatures() {
  const [cfg, setCfg] = useState({ gonzaga_email: "", guilherme_email: "", livro: "01", gonzaga_has_pass: false, guilherme_has_pass: false });
  const [gp, setGp] = useState(""); const [hp, setHp] = useState("");
  const [saving, setSaving] = useState(false);
  const { canManage } = useAuth();
  useEffect(() => { api.get("/cert-config").then(({ data }) => setCfg(data)).catch(() => {}); }, []);
  const save = async () => {
    setSaving(true);
    try {
      await api.put("/cert-config", { gonzaga_email: cfg.gonzaga_email, guilherme_email: cfg.guilherme_email, livro: cfg.livro, gonzaga_password: gp || undefined, guilherme_password: hp || undefined });
      toast.success("Configuração dos assinantes salva");
      setGp(""); setHp("");
      api.get("/cert-config").then(({ data }) => setCfg(data)).catch(() => {});
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  if (!canManage) return <p className="text-sm text-slate-400">Apenas gestores podem configurar os assinantes.</p>;
  return (
    <div className="max-w-2xl space-y-5" data-testid="ead-assinaturas">
      <div className="rounded-xl border border-emerald-200 bg-emerald-50/50 p-4">
        <p className="text-sm text-slate-700">O Gonzaga e o Guilherme assinam os certificados no portal próprio (com o dedo, no tablet). Quando um aluno é aprovado, eles recebem um e-mail avisando que há certificados a assinar.</p>
        <a href="/assinaturas" target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center rounded-md bg-[#0B2545] px-3 py-1.5 text-sm font-semibold text-white hover:bg-[#134074]" data-testid="open-signer-portal">Abrir portal de assinaturas ↗</a>
      </div>
      <div className="rounded-xl border border-[#E2E8F0] bg-white p-4 shadow-sm">
        <p className="mb-2 text-sm font-bold text-[#0B2545]">Eng. Dr. Luiz Gonzaga da Costa Neto <span className="font-normal text-slate-400">(login: gonzaga)</span></p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div><Label className="mb-1 block text-xs font-semibold text-slate-600">E-mail (avisos)</Label><Input value={cfg.gonzaga_email || ""} onChange={(e) => setCfg((p) => ({ ...p, gonzaga_email: e.target.value }))} data-testid="gonzaga-email" /></div>
          <div><Label className="mb-1 block text-xs font-semibold text-slate-600">{cfg.gonzaga_has_pass ? "Nova senha (opcional)" : "Definir senha"}</Label><Input type="password" value={gp} onChange={(e) => setGp(e.target.value)} placeholder={cfg.gonzaga_has_pass ? "••••••" : "senha de acesso"} data-testid="gonzaga-pass" /></div>
        </div>
      </div>
      <div className="rounded-xl border border-[#E2E8F0] bg-white p-4 shadow-sm">
        <p className="mb-2 text-sm font-bold text-[#0B2545]">Guilherme Alves Vieira <span className="font-normal text-slate-400">(login: guilherme)</span></p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div><Label className="mb-1 block text-xs font-semibold text-slate-600">E-mail (avisos)</Label><Input value={cfg.guilherme_email || ""} onChange={(e) => setCfg((p) => ({ ...p, guilherme_email: e.target.value }))} data-testid="guilherme-email" /></div>
          <div><Label className="mb-1 block text-xs font-semibold text-slate-600">{cfg.guilherme_has_pass ? "Nova senha (opcional)" : "Definir senha"}</Label><Input type="password" value={hp} onChange={(e) => setHp(e.target.value)} placeholder={cfg.guilherme_has_pass ? "••••••" : "senha de acesso"} data-testid="guilherme-pass" /></div>
        </div>
      </div>
      <div className="flex items-end gap-3">
        <div><Label className="mb-1 block text-xs font-semibold text-slate-600">Livro nº</Label><Input className="w-28" value={cfg.livro || ""} onChange={(e) => setCfg((p) => ({ ...p, livro: e.target.value }))} data-testid="cert-livro" /></div>
        <Button onClick={save} disabled={saving} className="bg-[#0B2545] hover:bg-[#134074]" data-testid="sig-save">{saving ? "Salvando..." : "Salvar"}</Button>
      </div>
    </div>
  );
}

export default function EadAdmin() {
  return (
    <div data-testid="ead-admin">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-3">
        <p className="text-sm text-emerald-900"><b>Loja de cursos (venda externa):</b> alunos de fora da Valbrac compram e acessam pela página pública.</p>
        <a href="/cursos" target="_blank" rel="noreferrer" data-testid="ead-open-store">
          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700"><GraduationCap className="mr-1.5 h-4 w-4" /> Abrir catálogo público (/cursos)</Button>
        </a>
      </div>
      <Tabs defaultValue="cursos">
        <TabsList className="mb-4">
          <TabsTrigger value="cursos" data-testid="ead-tab-cursos"><GraduationCap className="mr-1.5 h-4 w-4" /> Cursos (NRs)</TabsTrigger>
          <TabsTrigger value="atribuicoes" data-testid="ead-tab-atribuicoes"><Users className="mr-1.5 h-4 w-4" /> Atribuições</TabsTrigger>
          <TabsTrigger value="acompanhamento" data-testid="ead-tab-acompanhamento"><BarChart3 className="mr-1.5 h-4 w-4" /> Acompanhamento</TabsTrigger>
          <TabsTrigger value="assinaturas" data-testid="ead-tab-assinaturas"><PenLine className="mr-1.5 h-4 w-4" /> Assinaturas</TabsTrigger>
        </TabsList>
        <TabsContent value="cursos"><Cursos /></TabsContent>
        <TabsContent value="atribuicoes"><Atribuicoes /></TabsContent>
        <TabsContent value="acompanhamento"><Acompanhamento /></TabsContent>
        <TabsContent value="assinaturas"><Signatures /></TabsContent>
      </Tabs>
    </div>
  );
}
